/**
 * Wishlist Hook — VTEX wish-list app (vtex.wish-list@1.x), invoked through
 * the loaders/actions registered in ~/setup/commerce-loaders.ts.
 *
 * Mirrors the original apps/vtex/hooks/useWishlist.ts + ~/hooks/useUser.ts
 * pair: module-level state shared by every consumer, loaded once per page
 * and refreshed when the tab regains visibility (login finishes in a popup
 * or another tab).
 */
import { useEffect } from "react";
import { useStore } from "@tanstack/react-store";
import { signal } from "~/sdk/signal";
import { invoke } from "~/runtime";

export interface WishlistItemRef {
  sku: string;
  productId: string;
}

export interface WishlistItem {
  id: string;
  productId: string;
  sku: string;
  title?: string;
}

const items = signal<WishlistItem[]>([]);
const loading = signal(false);

let initStarted = false;

async function loadWishlist() {
  loading.value = true;
  try {
    items.value = (await invoke.vtex.loaders.wishlist()) ?? [];
  } catch (err) {
    console.error("[useWishlist] load failed:", err);
  } finally {
    loading.value = false;
  }
}

export function useWishlist() {
  // The module-level `signal()` is @tanstack/store-backed and does NOT
  // auto-subscribe on `.value` reads inside render (see ~/sdk/signal.ts) —
  // subscribe explicitly so consumers re-render when the list changes.
  useStore(items.store, (s) => s);
  useStore(loading.store, (s) => s);

  useEffect(() => {
    if (initStarted) return;
    initStarted = true;
    loadWishlist();
    document.addEventListener(
      "visibilitychange",
      () => document.visibilityState === "visible" && loadWishlist(),
    );
  }, []);

  return {
    loading,
    async addItem(item: WishlistItemRef) {
      loading.value = true;
      try {
        items.value = (await invoke.vtex.actions.wishlist.addItem(item)) ?? [];
      } finally {
        loading.value = false;
      }
    },
    async removeItem(ref: { id: string }) {
      loading.value = true;
      try {
        items.value = (await invoke.vtex.actions.wishlist.removeItem(ref)) ?? [];
      } finally {
        loading.value = false;
      }
    },
    getItem(item: WishlistItemRef): WishlistItem | null {
      return (
        items.value.find(
          (it) => String(it.sku) === String(item.sku) &&
            String(it.productId) === String(item.productId),
        ) ?? null
      );
    },
  };
}

export default useWishlist;
